import { useCallback, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { ColumnDef } from '@tanstack/table-core';

// Services
import { getFleetDevices, getFleetSummary } from '../../../../services/fleet.services';

// Utils
import { Storage } from '../../../../utils/Storage';
import { makeFleetFacilityColumns } from '../../../../utils/columns/makeFleetFacilityColumns';
import { makeSightingListColumn } from '../../../../utils/columns/makeSightingListColumn';

// Common components
import { useNewTable } from '../../../common/table/index';

// Types
import { DeviceEnriched } from '../../../../types/DeviceEnriched';
import { FleetSummary } from '../../../../types/Fleetsummary';
import { PaginatedResult } from '../../../../types/PaginatedResult';

// Components
import GlobalFleetMap from './GlobalFleetMap';
import FleetFilterPanel from './FleetFilterPanel';
import Devices from '../../device/deviceList';

type SelectedFacility = { facilityId: string; facilityName: string; stationIataCode?: string };

export default function GlobalFleetDashboard() {
  const restored = useMemo(() => Storage.getFleetDashboardState(), []);

  const [paramFilter, setParamFilter] = useState<{ filters: any[] }>(() => ({ filters: restored?.filters ?? [] }));
  const [selectedFacility, setSelectedFacility] = useState<SelectedFacility | null>(() => restored?.facility ?? null);

  const [panFacilityId, setPanFacilityId] = useState<string | null>(() => restored?.facility?.facilityId ?? null);

  const deviceListRef = useRef<HTMLDivElement | null>(null);

  const columns = useMemo(() => makeFleetFacilityColumns(), []);

  // The embedded Device Details table shows the sighting-style column set
  // (sensor readings, pairing, reader) rather than the Devices page's own columns.
  const deviceColumns = useMemo(() => makeSightingListColumn({ hideReader: true }) as ColumnDef<DeviceEnriched>[], []);

  const handleSelectFacility = useCallback((facility: FleetSummary) => {
    const next: SelectedFacility = {
      facilityId: facility.facilityId,
      facilityName: facility.facilityName,
      stationIataCode: facility.stationIataCode,
    };
    setSelectedFacility(next);
    setPanFacilityId(facility.facilityId);
    Storage.setFleetDashboardState({ facility: next });
  }, []);

  const handleSelectFacilityFromMap = useCallback(
    (facility: FleetSummary) => {
      handleSelectFacility(facility);
      deviceListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [handleSelectFacility],
  );

  const handleFetchComplete = useCallback((res: PaginatedResult<FleetSummary> | undefined) => {
    const results = res?.results ?? [];
    setSelectedFacility((prev) => {
      if (prev) return prev;
      const first = results[0];
      return first ? { facilityId: first.facilityId, facilityName: first.facilityName, stationIataCode: first.stationIataCode } : null;
    });
  }, []);

  // The filter panel's reset/refresh icon drops the saved view along with the filters.
  const handleResetFilters = useCallback(() => {
    Storage.clearFleetDashboardState();
    setSelectedFacility(null);
    setPanFacilityId(null);
  }, []);

  // One table instance - its `data` drives BOTH the map and the facility
  const table = useNewTable<FleetSummary>({
    columns,
    fetchAsync: getFleetSummary,
    params: paramFilter,
    serverSide: true,
    onRowClick: handleSelectFacility,
    onFetchComplete: handleFetchComplete,
  });

  const facilities = (table.data as FleetSummary[]) ?? [];

  return (
    <Box sx={{ pl: 0, pr: 2, pb: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        {/* Left: map — 60% */}
        <Box sx={{ flex: { md: '0 0 60%' }, minWidth: 0 }}>
          <GlobalFleetMap
            facilities={facilities}
            loading={table.loading}
            selectedFacilityId={selectedFacility?.facilityId ?? null}
            panFacilityId={panFacilityId}
            onSelectFacility={handleSelectFacilityFromMap}
          />
        </Box>

        {/* Right: station & facility filter — 40% */}
        <Box sx={{ flex: { md: '0 0 40%' }, minWidth: 0 }}>
          <FleetFilterPanel
            table={table}
            columns={columns}
            paramFilter={paramFilter}
            setParamFilter={setParamFilter}
            onReset={handleResetFilters}
            selectedFacilityId={selectedFacility?.facilityId ?? null}
          />
        </Box>
      </Box>

      {/* Below: device details for the selected station/facility */}
      <Box ref={deviceListRef} sx={{ mt: 2, scrollMarginTop: 16 }}>
        <Devices
          title='Device Details'
          hideSearchBar
          hideToolbarActions
          fetchAsync={getFleetDevices}
          columns={deviceColumns}
          facilityId={selectedFacility?.facilityId ?? null}
          facilityName={selectedFacility?.facilityName ?? null}
          stationIataCode={selectedFacility?.stationIataCode ?? null}
        />
      </Box>
    </Box>
  );
}
